import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

import { encode, decode } from 'base64-ts';
import { IEnumItem } from '@enums/enum-item';
import { RcFile } from 'antd/es/upload';

dayjs.extend(utc);
dayjs.extend(tz);

export function sanitize(text: string, allowedTags: Array<string> = ['b', 'i', 'u', 'br', 'br', 'div']) {
    return (
        text &&
        text.replace(/<[^>]*>/gm, (tag, a, b, c, d) => {
            let at = allowedTags.find((x) => tag.startsWith('<' + x));
            if (at) return '<' + at + '>';
            at = allowedTags.find((x) => tag === '</' + x + '>');
            if (at) return tag;
            return '';
        })
    );
}

export function capitalizeFirstLetter(string: string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : string;
}

export function distinct(arr: Array<any>) {
    const distinctArray: Array<any> = [];
    arr.forEach((x) => !distinctArray.includes(x) && distinctArray.push(x));
    return distinctArray;
}

export function getBase64Blob(file: Blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

export const getBase64RcFile = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

export function jsonParse(value: string, errValue = undefined) {
    let retval = errValue;
    try {
        retval = JSON.parse(value);
    } catch (err) {
        console.error('json parse error', value, err);
    }
    return retval;
}

export function object2Array(obj: any, keyName: string) {
    return Object.keys(obj).map((k) => ({ ...obj[k], [keyName]: k }));
}

export const get = (array: Array<any> = [], func: any) => {
    let result;
    Object.values(array).forEach((item) => {
        if (func(item)) {
            result = item;
        }
    });
    return result;
};

export const toUpperCase = (value: string) => {
    if (!value || typeof value !== 'string') return null;

    return value.toUpperCase();
};

export const dataURItoBlob = (dataURI: string) => {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    const byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    const blob = new Blob([ab], { type: mimeString });
    return blob;
};

export const getBoolean = (value: string) => {
    if (!value) return false;

    return value.toUpperCase() === 'TRUE';
};

export const arrayToBase64 = (array: Array<string>) => {
    const utf8Encoder = new TextEncoder();
    return encode(utf8Encoder.encode(JSON.stringify(array)));
};

export const base64ToArray = (value: string) => {
    const utf8Decoder = new TextDecoder();
    const toJson = utf8Decoder.decode(decode(value));
    const arr = JSON.parse(toJson);

    return arr;
};

export const getEnumList = (enums: any, getLabel?: any) => {
    const list: Array<IEnumItem> = [];
    Object.values(enums).map((key) => {
        if (!isNaN(Number(key))) {
            list.push({ label: getLabel ? getLabel(key) : key, value: Number(key) });
        }
    });

    return list;
};

export const tryParseJson = (json: string) => {
    try {
        return JSON.parse(json);
    } catch (error) {
        return null;
    }
};

export function toUrl(data: any) {
    return encodeURIComponent(JSON.stringify(data));
}

let timeout: any = undefined;
export function delayAction(callbac: () => void, ms?: number) {
    if (!ms) ms = 700;

    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(callbac, ms);
}

export const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
    arr.reduce(
        (groups, item) => {
            (groups[key(item)] ||= []).push(item);
            return groups;
        },
        {} as Record<K, T[]>,
    );

export const toFinanceString = (value: number | undefined | null, decimalPrecision = 2) => {
    if (!value) return value;

    return value.toLocaleString('ru-RU', { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision });
};

export const isNewYearAroundUs = () => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;

    return (month == 12 && day >= 14) || (month == 1 && day <= 10);
};

export const getDate = (date: Dayjs | undefined) => {
    if (!date) return '';

    return dayjs.utc(date).local().format('DD.MM.YYYY');
};

export const getDateTimeLocal = (date: Dayjs | undefined) => {
    if (!date) return '';

    return toDayjsLocal(date)?.format('DD.MM.YYYY HH:mm');
};

export const toDayjsLocal = (date: Dayjs | undefined): Dayjs | undefined => {
    if (!date) return undefined;

    const timeZone = dayjs.tz.guess();

    return dayjs.utc(date).tz(timeZone, false);
};

export const toDayjsUtc = (date: Dayjs | undefined): Dayjs | undefined => {
    if (!date) undefined;

    return dayjs.utc(date).tz('UTC', false);
};

export const sortValues = (a: string | number | Dayjs | boolean | undefined, b: string | number | Dayjs | boolean | undefined) => {
    if (a === b) return 0;
    else if (!a) return -1;
    else if (!b) return 1;

    if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b);
    } else if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    } else if (typeof a === 'boolean' && typeof b === 'boolean') {
        return Number(a) - Number(b);
    } else if (a instanceof Dayjs && b instanceof Dayjs) {
        return a.diff(b, 'second');
    }

    return 0;
};
